// Primera linea a proposito: `efectos.ts` es el unico sitio donde viven los imports que solo
// existen por su efecto secundario, y tienen que correr antes que nada. Ver el modulo.
import './config/efectos';
import './shared/assets/font/orkney/orkney.scss';
import { initializeInversify } from './config/inversify-config';
import ReactDOM from 'react-dom/client';
import 'antd/dist/reset.css';
import './shared/assets/styles/app.scss';
import './index.scss';
import esES from 'antd/locale/es_ES';
import 'dayjs/locale/es';
import './i18n';
import { ConfigProvider } from 'antd';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AuthProvider } from 'react-oidc-context';
import { routes } from './config/routes';
import { configAxios } from './shared/configs/axios-config';
import { ensureFreshSession, getAuthProviderProps, getOidcConfig, watchSessionChanges } from './shared/configs/auth-config';
import { configAnt, darkVariant, themeConfig } from './shared/configs/ant-theme-config';
import config from './config/app-config';
import { ConfigProviderProps } from 'antd/es/config-provider';
import 'quill/dist/quill.snow.css';
import { UserNotificationsProvider } from './pages/notifications/user-notifications-context';
import { FaqContextProvider } from './pages/faq/faq-context';
import ErrorPage from './error-page';
import i18n from './i18n';
import CookiebotProvider from './shared/components/cookiebot-provider';
import UserAuthGuard from './pages/notifications/user-auth-guard';
import SessionGuard from './shared/components/session-guard';
import { HelmetProvider } from 'react-helmet-async';
import Analytics, { META_PIXEL_ID, safeFbq } from './analytics';
import { CvAnalysisProvider } from './contexts/cv-analysis-context';
import { EnterpriseLinkedinAnalysisProvider } from './contexts/enterprise-linkedin-analysis-context';
import { getFbqUserData } from './meta-pixel';
import { BugReportProvider } from './common/bug-report/bug-report-context';
import { installMessageErrorHint } from './common/bug-report/install-message-error-hint';
import { migrateLegacyLocalStorage } from './common/j2l-cache';
// import { HubSpotLiveChatContextProvider } from 'src/shared/components/hubspot-live-chat';
// import ErrorPage from './error-page';
// import i18n from './i18n';

// Nombre y version del paquete. Bajo CRA llegaban por `.env` como `REACT_APP_*`; Vite no lee ese
// fichero, y `process.env` se compila a `{}`, asi que las dos salian `undefined` sin que nada fallara.
// Las inyecta ahora el bloque `define` de `vite.config.mts`, leidas del propio package.json.
console.log(`${import.meta.env.VITE_APP_NAME} ${import.meta.env.VITE_APP_VERSION}`);

const getNormalizedPathname = (pathname: string) => {
  const normalizedPath = pathname.replace(/^\/website(?=\/|$)/, '');
  return normalizedPath.length > 0 ? normalizedPath : '/';
};

const shouldSkipDev401Redirect = (pathname: string) => {
  const normalizedPath = getNormalizedPathname(pathname);
  const publicPaths = ['/default-user-page', '/shortlists/demo', '/shortlists/demo/generate', '/landings'];
  return publicPaths.some((publicPath) => normalizedPath === publicPath || normalizedPath.startsWith(`${publicPath}/`));
};

// configOidcLogging();
configAnt();
configAxios({
  baseUrl: config.baseUrl,
  useBearerToken: {
    authority: config.authority,
    clientId: config.clientId
  },
  acceptOnlyJsonResponses: true,
  onHttp401Error:
    process.env.NODE_ENV !== 'development'
      ? undefined
      : (data) => {
          console.error('HTTP 401', { data });
          const url = new URL(window.location.href);
          if (shouldSkipDev401Redirect(url.pathname)) return;
          window.location.href = `${window.REACT_APP_API_BASE_URI}/default-user-page?returnUrl=${window.location.href}`;
        }
});
const oidcConfig = getOidcConfig({
  authority: config.authority,
  clientId: config.clientId,
  clientSecret: config.clientSecret,
  postLogoutRedirectUri: config.postLogoutRedirectUri,
  // redirectUri: config.redirectUri,
  redirectUri: `${window.REACT_APP_API_BASE_URI}/default-user-page`,
  silentRedirectUri: config.redirectUri,
  changePostLoginUrl: (user, currentPostLoginUrl) => {
    // if (user) return `${window.REACT_APP_API_BASE_URI}/default-user-page?returnUrl=${currentPostLoginUrl ?? ''}`;
    // if (user && isNullOrWhiteSpaces(currentPostLoginUrl) && isInRole(user, 'Person'))
    //   return `${window.REACT_APP_API_BASE_URI}/services/my-profile`;
    // if (user && isNullOrWhiteSpaces(currentPostLoginUrl) && isInRole(user, J2LRole.EnterpriseUser))
    //   return `${window.REACT_APP_API_BASE_URI}/onboarding/enterprises/registration-completed`;
    return currentPostLoginUrl;
  }
});

const currentOnSigninCallbackFn = oidcConfig.onSigninCallback;

oidcConfig.onSigninCallback = async (user) => {
  currentOnSigninCallbackFn?.(user);
  if (user) {
    const userData = await getFbqUserData({ user });
    safeFbq('init', META_PIXEL_ID, userData);
  }
};

installMessageErrorHint();

// One-off: fold the legacy scattered localStorage keys into the centralized `j2l-cache` layout.
migrateLegacyLocalStorage();

export const appConfigProviderProps: ConfigProviderProps = {
  // El boton de limpiar de los campos, legible y alcanzable. El detalle de las medidas esta en
  // `index.scss`, junto a las reglas; aqui va solo el enganche.
  //
  // Se hace por `classNames.clear`, el slot semantico que antd 6 expone para ese nodo en los dos
  // componentes, y no con un selector `.ant-*`, que es acoplarse al DOM interno de la libreria. No es
  // una regla absoluta de esta aplicacion -quedan reglas `.ant-*` en `index.scss` para cosas que el
  // slot no sabe expresar, como el margen del mensaje de error, que va acotado a `-with-help`-, pero
  // aqui no hace falta.
  //
  // Y por `classNames` y no por `styles`, que seria un estilo en linea: le ganaria tambien a los
  // estados de antd y dejaria el icono sin `:active` ni contorno de foco.
  select: { classNames: { clear: 'j2l-select-clear' } },
  input: { classNames: { clear: 'j2l-input-clear' } },
  locale: {
    ...esES,
    Tour: {
      Next: 'Siguiente',
      Previous: 'Anterior',
      Finish: 'Finalizar'
    },
    QRCode: {
      expired: 'QR expirado',
      refresh: 'Refrescar'
    }
  },
  theme: {
    ...themeConfig,
    components: {
      Button: {
        colorBgContainerDisabled: '#F1EDE9',
        colorTextDisabled: '#CBCED4'
      },
      Checkbox: {
        paddingXS: 10,
        borderRadiusSM: 2,
        ...darkVariant
      },
      Input: {
        borderRadiusLG: 4,
        colorPrimaryBorderHover: '#37383b',
        colorPrimaryHover: '#2b2c2e',
        colorPrimaryActive: '#000000'
      },
      Select: {
        borderRadiusLG: 4,
        ...darkVariant
      },
      Radio: {
        ...darkVariant
      },
      Slider: {
        ...darkVariant
      },
      Switch: {
        ...darkVariant,
        colorTextQuaternary: '#7F88A1'
      },
      Tabs: {
        ...darkVariant
      },
      Typography: {
        ...darkVariant
      },
      DatePicker: {
        ...darkVariant,
        paddingXXS: 4,
        borderRadiusLG: 4,
        borderRadius: 4
      },
      InputNumber: {
        colorPrimaryBorderHover: '#37383b',
        colorPrimaryHover: '#2b2c2e',
        colorPrimaryActive: '#000000',
        borderRadiusLG: 4
      },
      Segmented: {
        ...darkVariant,
        colorBgElevated: '#1A1C21',
        colorText: 'white'

        //   itemSelectedBg: "rgb(0,0,0)",
        // itemSelectedColor: "rgba(255,255,255,0.88)"
      }
    }
  }
};

const rootNode = document.getElementById('root') as HTMLElement;

initializeInversify()
  .then(async (_) => {
    // Antes de montar nada: si el usuario que hay en localStorage no es el de la sesion viva, esta
    // llamada lo resuelve, adoptando la viva. Se espera a proposito: renderizar antes seria pintar
    // un frame de la aplicacion del usuario equivocado.
    await ensureFreshSession(oidcConfig);
    watchSessionChanges(oidcConfig);

    // `getAuthProviderProps` construye el `UserManager` y se lo pasa a `AuthProvider` en vez de
    // dejar que lo construya el. Es lo que permite a `session-guard` renovar por el manager real y no
    // por `auth.signinSilent`, cuyo envoltorio despacha `activeNavigator` y dispara el velo a
    // pantalla completa de los layouts en cada renovacion de fondo. Va aqui, despues de
    // `ensureFreshSession`, porque el constructor del manager ya arranca la renovacion silenciosa.
    const authProviderProps = getAuthProviderProps(oidcConfig);

    const router = createBrowserRouter(routes, { basename: '/website' });
    const root = ReactDOM.createRoot(rootNode);

    root.render(
      // https://stackoverflow.com/a/62237101/11567125
      // <React.StrictMode>
      // Aqui NO va un <Helmet> raiz. Lo hubo, repitiendo palabra por palabra el titulo y la
      // descripcion estaticos de index.html, y con react-helmet-async 3 bajo React 19 eso deja de
      // ser inofensivo: la libreria ya no reconcilia entre instancias, cada <Helmet> crea su propio
      // elemento <title>, y react-dom 19 no deduplica ni title ni meta. El resultado eran tres de
      // cada en las paginas con <SEO>. El titulo por pagina lo pone <SEO> (src/common/seo.tsx) y el
      // de respaldo, index.html. Lo pincha src/common/contrato-head.test.tsx.
      <HelmetProvider>
        <AuthProvider {...authProviderProps}>
          <UserAuthGuard>
            <ConfigProvider {...appConfigProviderProps}>
              {/*
                `<SessionGuard>` mantiene viva la renovacion del token y avisa cuando ya no se puede
                renovar. Va DENTRO de `<ConfigProvider>` porque su modal es declarativo, asi que
                hereda de aqui el tema y el idioma; colgado mas arriba saldria con los colores por
                defecto de antd.
              */}
              <SessionGuard>
                {/*
                `<Analytics>` va DENTRO de `<ConfigProvider>` a proposito: no envuelve nada que
                necesite, solo pinta `{children}` y efectos, y desde aqui su modal de confirmacion
                hereda el tema en vez de salir con los colores por defecto de antd y veinte lineas de
                estilos en linea compensandolo.
              */}
                <Analytics active={window.REACT_APP_ENV === 'prod'}>
                  {/*
                  El script de Cookiebot ya lo carga `index.html`, en el <head> y antes que
                  nada, que es lo unico que permite al bloqueo automatico llegar a tiempo. El
                  provider lo detecta por el id `Cookiebot` y no vuelve a inyectarlo: aqui se queda
                  para dar `useCookiebot()` y el flujo de rechazo. `blockingMode` va igualmente para
                  que su inyeccion de respaldo, si alguna vez es la que actua, no vuelva al modo
                  manual que no bloquea nada.
                */}
                  <CookiebotProvider
                    cbid={window.REACT_APP_COOKIEBOT_KEY}
                    useRejectNeededCookiesFlow={false}
                    active={window.REACT_APP_ENV === 'prod'}
                    blockingMode="auto"
                  >
                    <FaqContextProvider>
                      <UserNotificationsProvider>
                        <BugReportProvider>
                          <CvAnalysisProvider>
                            <EnterpriseLinkedinAnalysisProvider>
                              {/* <HubSpotLiveChatContextProvider portalId={window.REACT_APP_HUBSPOT__PORTAL_ID} loadImmediately={false}> */}
                              <RouterProvider router={router} />
                              {/* </HubSpotLiveChatContextProvider> */}
                            </EnterpriseLinkedinAnalysisProvider>
                          </CvAnalysisProvider>
                        </BugReportProvider>
                      </UserNotificationsProvider>
                    </FaqContextProvider>
                  </CookiebotProvider>
                </Analytics>
              </SessionGuard>
            </ConfigProvider>
          </UserAuthGuard>
        </AuthProvider>
      </HelmetProvider>
      // </React.StrictMode>
    );
  })
  .catch((_) => {
    const root = ReactDOM.createRoot(rootNode);

    root.render(
      // https://stackoverflow.com/a/62237101/11567125
      // <React.StrictMode>
      // Aqui NO va un <Helmet> raiz. Lo hubo, repitiendo palabra por palabra el titulo y la
      // descripcion estaticos de index.html, y con react-helmet-async 3 bajo React 19 eso deja de
      // ser inofensivo: la libreria ya no reconcilia entre instancias, cada <Helmet> crea su propio
      // elemento <title>, y react-dom 19 no deduplica ni title ni meta. El resultado eran tres de
      // cada en las paginas con <SEO>. El titulo por pagina lo pone <SEO> (src/common/seo.tsx) y el
      // de respaldo, index.html. Lo pincha src/common/contrato-head.test.tsx.
      <HelmetProvider>
        <ConfigProvider {...appConfigProviderProps}>
          <Analytics active={window.REACT_APP_ENV === 'prod'}>
            <BugReportProvider>
              {/* <HubSpotLiveChatContextProvider portalId={window.REACT_APP_HUBSPOT__PORTAL_ID} loadImmediately={false}> */}
              <RouterProvider
                router={createBrowserRouter(
                  [
                    {
                      path: '*',
                      element: (
                        <ErrorPage
                          errorMessage={i18n.t('La app no pudo iniciar')}
                          redirectTo={{ label: i18n.t('Recargar'), onClick: () => window.location.reload() }}
                        />
                      ),
                      errorElement: <ErrorPage />
                    }
                  ],
                  { basename: '/website' }
                )}
              />
              {/* </HubSpotLiveChatContextProvider> */}
            </BugReportProvider>
          </Analytics>
        </ConfigProvider>
      </HelmetProvider>
      // </React.StrictMode>
    );
  });
